/**
 * useRealtimeMessages Hook
 * Real-time messaging with WebSocket + REST fallback
 * Based on ImpactIQ v2 architecture patterns
 *
 * Features:
 * - WebSocket connection with heartbeat (ping/pong)
 * - Typing indicators with debounce and auto-stop
 * - Online presence tracking
 * - Automatic reconnection with exponential backoff
 * - Cursor-based pagination for message history
 * - Optimistic updates with rollback
 * - Message reactions support
 * - REST fallback when WebSocket fails
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://onlyjalen-api.jalen1wa.workers.dev';
const WS_BASE = API_BASE.replace('https://', 'wss://').replace('http://', 'ws://');

// Heartbeat configuration
const PING_INTERVAL = 25000; // Send ping every 25 seconds
const PONG_TIMEOUT = 10000;  // Expect pong within 10 seconds

// Reconnection settings
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000];

// Typing indicator settings
const TYPING_DEBOUNCE = 500;
const TYPING_AUTO_STOP = 3000;

export interface Reaction {
  emoji: string;
  count: number;
  users: string[];
  includesMe: boolean;
}

export interface Message {
  id: string;
  from_user_id: string;
  to_user_id: string;
  content: string;
  read: boolean;
  created_at: string;
  updated_at?: string;
  sender_name?: string;
  sender_avatar?: string;
  fromDisplayName?: string;
  status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  attachments?: string[];
  reactions?: Reaction[];
  edited?: boolean;
}

export interface UseRealtimeMessagesReturn {
  messages: Message[];
  connected: boolean;
  connecting: boolean;
  otherUserTyping: boolean;
  otherUserOnline: boolean;
  error: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  sendMessage: (content: string, attachments?: string[]) => Promise<boolean>;
  editMessage: (messageId: string, content: string) => Promise<boolean>;
  deleteMessage: (messageId: string, opts?: { hard?: boolean }) => Promise<boolean>;
  toggleReaction: (messageId: string, emoji: string) => Promise<boolean>;
  sendTyping: () => void;
  markAsRead: () => void;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Transform API message format to frontend format
 * API uses: sender_id, sender_name, sender_avatar
 * Frontend uses: from_user_id, to_user_id, sender_name
 */
function normalizeMessage(apiMsg: any): Message {
  return {
    id: apiMsg.id,
    // Map sender_id to from_user_id (API field → frontend field)
    from_user_id: apiMsg.from_user_id || apiMsg.sender_id || apiMsg.fromUserId || '',
    to_user_id: apiMsg.to_user_id || apiMsg.toUserId || '',
    content: apiMsg.content || '',
    read: apiMsg.read ?? false,
    created_at: apiMsg.created_at || apiMsg.createdAt || new Date().toISOString(),
    updated_at: apiMsg.updated_at || apiMsg.updatedAt,
    sender_name: apiMsg.sender_name || apiMsg.fromDisplayName || apiMsg.senderName,
    sender_avatar: apiMsg.sender_avatar || apiMsg.senderAvatar,
    fromDisplayName: apiMsg.fromDisplayName || apiMsg.sender_name,
    status: apiMsg.status || 'sent',
    attachments: typeof apiMsg.attachments === 'string'
      ? (() => { try { return JSON.parse(apiMsg.attachments); } catch { return []; } })()
      : (apiMsg.attachments || []),
    reactions: apiMsg.reactions,
    edited: !!apiMsg.edited_at || !!apiMsg.editedAt,
  };
}

export function useRealtimeMessages(partnerId: string): UseRealtimeMessagesReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [otherUserOnline, setOtherUserOnline] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTypingSentRef = useRef<number>(0);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pongTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingStopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  // Re-read the token at each fetch so REST calls survive a mid-session
  // access-token rotation. The previous `const token = ...` captured the
  // token at hook-init and went stale after auth refresh — sendMessage
  // would 401 even though the user was still signed in.
  const getToken = () => localStorage.getItem('onlyjalen_access_token');
  // Backwards-compat: a few legacy lines below still reference `token`
  // as a value (e.g., when building headers right away). Replaced by
  // getToken() inline; this binding is kept only to satisfy a stale
  // import if any survived. Will fall back to "" if storage cleared.
  const token = getToken() || '';

  // Clear all heartbeat timers
  const clearHeartbeat = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (pongTimeoutRef.current) {
      clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  }, []);

  // Start heartbeat mechanism
  const startHeartbeat = useCallback(() => {
    clearHeartbeat();

    pingIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        // Send ping
        wsRef.current.send(JSON.stringify({ type: 'ping' }));

        // Set pong timeout
        pongTimeoutRef.current = setTimeout(() => {
          console.log('[WS] Pong timeout - reconnecting');
          wsRef.current?.close(4000, 'Pong timeout');
        }, PONG_TIMEOUT);
      }
    }, PING_INTERVAL);
  }, [clearHeartbeat]);

  // REST API for loading messages with cursor-based pagination
  const loadMessagesViaREST = useCallback(async (beforeMessageId?: string) => {
    if (!token || !partnerId) return;

    try {
      const url = beforeMessageId
        ? `${API_BASE}/api/messages/conversations/${partnerId}/messages?before=${beforeMessageId}&limit=50`
        : `${API_BASE}/api/messages/conversations/${partnerId}/messages?limit=50`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.messages) {
          // Normalize API messages to frontend format
          const normalized = data.messages.map(normalizeMessage);
          if (beforeMessageId) {
            // Prepend older messages
            setMessages(prev => [...normalized, ...prev]);
          } else {
            // Initial load
            setMessages(normalized);
          }
          setHasMore(data.messages.length === 50);
        }
      }
    } catch (err) {
      console.error('Failed to load messages via REST:', err);
    }
  }, [partnerId, token]);

  // Start polling as fallback
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;

    console.log('[Polling] Starting fallback polling');
    loadMessagesViaREST();

    pollingIntervalRef.current = setInterval(() => {
      loadMessagesViaREST();
    }, 5000);
  }, [loadMessagesViaREST]);

  // Handle incoming WebSocket messages
  const handleWSMessage = useCallback((data: any) => {
    switch (data.type) {
      case 'pong':
        // Clear pong timeout on successful pong
        if (pongTimeoutRef.current) {
          clearTimeout(pongTimeoutRef.current);
          pongTimeoutRef.current = null;
        }
        break;

      case 'sync':
        // Initial sync with existing messages
        if (data.messages) {
          // Normalize messages from WebSocket sync
          setMessages(data.messages.map(normalizeMessage));
          setHasMore(data.hasMore ?? false);
        }
        if (data.onlineParticipants?.length > 1) {
          setOtherUserOnline(true);
        }
        break;

      case 'message': {
        // Broadcasts arrive as { type: 'message', message: { id, sender_id, ... } }
        // Unwrap the inner message object if present
        const msgPayload = (data.message && typeof data.message === 'object')
          ? (data.message as Record<string, unknown>)
          : data;

        setMessages(prev => {
          // Deduplicate by real ID (covers the case where optimistic update was already
          // resolved to the real ID before the WS broadcast arrived)
          const existingIndex = prev.findIndex(
            m => m.id === msgPayload.id || m.id === (msgPayload.tempId as string | undefined)
          );
          if (existingIndex !== -1) {
            const updated = [...prev];
            updated[existingIndex] = {
              ...updated[existingIndex],
              id: msgPayload.id as string,
              status: 'sent',
            };
            return updated;
          }
          return [...prev, normalizeMessage(msgPayload)];
        });
        break;
      }

      case 'message_edited':
        setMessages(prev => prev.map(m =>
          m.id === data.messageId
            ? { ...m, content: data.content, edited: true, updated_at: data.updatedAt }
            : m
        ));
        break;

      case 'message_deleted':
        setMessages(prev => prev.filter(m => m.id !== data.messageId));
        break;

      case 'reaction':
        setMessages(prev => prev.map(m =>
          m.id === data.messageId
            ? { ...m, reactions: data.reactions }
            : m
        ));
        break;

      case 'typing':
        setOtherUserTyping(true);
        // Auto-clear typing after 3 seconds
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        typingTimeoutRef.current = setTimeout(() => {
          setOtherUserTyping(false);
        }, TYPING_AUTO_STOP);
        break;

      case 'typing_stop':
        setOtherUserTyping(false);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
        break;

      case 'presence':
        if (data.action === 'joined') {
          setOtherUserOnline(true);
        } else if (data.action === 'left') {
          setOtherUserOnline(data.onlineCount > 1);
        }
        break;

      case 'read':
        // Update message status to read
        setMessages(prev => prev.map(m => ({
          ...m,
          read: true,
          status: m.status === 'sent' || m.status === 'delivered' ? 'read' : m.status,
        })));
        break;

      case 'error':
        console.error('[WS] Server error:', data.message);
        setError(data.message);
        break;
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!token || !partnerId) return;
    // Reject if a connection is already open. For CONNECTING, only reject
    // if the handshake started within the last 5s — past that, the socket
    // is likely wedged (TCP SYN with no SYN-ACK from CF edge, network
    // hiccup, etc.) and a refresh()/manual reconnect would otherwise stall
    // indefinitely. Force-close stale CONNECTING and try fresh.
    const cur = wsRef.current;
    const rs = cur?.readyState;
    if (rs === WebSocket.OPEN) return;
    if (rs === WebSocket.CONNECTING) {
      const startedAt = (cur as any).__createdAt as number | undefined;
      if (startedAt && Date.now() - startedAt < 5000) return;
      try { cur!.close(4001, 'CONNECTING timeout'); } catch {}
      wsRef.current = null;
    }

    setConnecting(true);
    setError(null);

    try {
      // Read token fresh on each connect so a rotated access token (from
      // the 1h refresh cycle) doesn't keep authenticating the WS with a
      // stale value. The earlier capture-at-hook-init left the WS dead
      // for users whose token rotated mid-session.
      const ws = new WebSocket(`${WS_BASE}/ws/${partnerId}?token=${getToken()}`);
      (ws as any).__createdAt = Date.now();
      wsRef.current = ws;

      ws.onopen = () => {
        // If a newer socket has already replaced this one in wsRef, this
        // open event is for an orphan — don't touch shared state.
        if (wsRef.current !== ws) {
          try { ws.close(1000, 'Superseded'); } catch {}
          return;
        }
        console.log('[WS] Connected to conversation:', partnerId);
        if (!mountedRef.current) {
          ws.close();
          return;
        }
        setConnected(true);
        setConnecting(false);
        reconnectAttempts.current = 0;

        // Start heartbeat
        startHeartbeat();

        // Stop polling if it was running
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        if (wsRef.current !== ws) return; // ignore orphan socket traffic
        try {
          const data = JSON.parse(event.data);
          handleWSMessage(data);
        } catch (err) {
          console.error('[WS] Failed to parse message:', err);
        }
      };

      ws.onclose = (event) => {
        // Only the *current* socket's close should mutate connection state
        // or trigger reconnect. If this is an orphan (replaced or kicked
        // by the DO for a newer connection from the same user), bail out.
        if (wsRef.current !== ws && wsRef.current !== null) return;

        console.log('[WS] Connection closed:', event.code, event.reason);
        setConnected(false);
        setConnecting(false);
        if (wsRef.current === ws) wsRef.current = null;
        clearHeartbeat();

        if (!mountedRef.current) return;
        // Suppress reconnect ONLY when the server explicitly told us this
        // socket was superseded by another (DO closes with reason
        // "Replaced by new connection" or "Superseded" when a newer WS
        // from the same user arrives). Code 1000 alone is too broad —
        // worker redeploys, hibernation, and idle timeouts all close 1000
        // and we DO want to reconnect after those.
        const reason = event.reason || '';
        if (event.code === 1000 && /supersed|replaced/i.test(reason)) return;

        // Attempt reconnection
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = RECONNECT_DELAYS[reconnectAttempts.current] || 16000;
          console.log(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1})`);
          setTimeout(() => {
            if (mountedRef.current) {
              reconnectAttempts.current++;
              connect();
            }
          }, delay);
        } else {
          // Fall back to polling
          console.log('[WS] Max reconnect attempts reached, falling back to polling');
          setError('Connection lost. Using fallback mode.');
          startPolling();
        }
      };

      ws.onerror = (event) => {
        if (wsRef.current !== ws) return; // ignore orphan errors
        console.error('[WS] Error:', event);
        setError('Connection error');
      };
    } catch (err) {
      console.error('[WS] Failed to create WebSocket:', err);
      setConnecting(false);
      setError('Failed to connect');
      startPolling();
    }
  }, [partnerId, token, handleWSMessage, startHeartbeat, clearHeartbeat, startPolling]);

  // Send a message with optimistic update and rollback
  // ALWAYS persist via REST API, use WebSocket only for real-time relay
  const sendMessage = useCallback(async (content: string, attachments?: string[]): Promise<boolean> => {
    if (!content.trim() && (!attachments || attachments.length === 0)) return false;

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const now = new Date().toISOString();

    // Optimistic update
    const optimisticMessage: Message = {
      id: tempId,
      from_user_id: 'self',
      to_user_id: partnerId,
      content: content.trim(),
      read: false,
      created_at: now,
      status: 'pending',
      attachments,
      reactions: [],
    };
    setMessages(prev => [...prev, optimisticMessage]);

    // Clear typing state
    setOtherUserTyping(false);

    // ALWAYS send via REST API for persistence
    try {
      const response = await fetch(`${API_BASE}/api/messages/conversations/${partnerId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: content.trim(),
          attachments,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Update optimistic message with real ID
        setMessages(prev => prev.map(m =>
          m.id === tempId
            ? { ...m, id: data.message?.id || data.id || data.messageId, status: 'sent' }
            : m
        ));
        return true;
      } else {
        // Rollback on failure
        const errorData = await response.json().catch(() => ({}));
        console.error('[WS] Send message failed:', errorData);
        setMessages(prev => prev.filter(m => m.id !== tempId));
        setError(errorData.error || 'Failed to send message');
        return false;
      }
    } catch (err) {
      // Rollback on error
      console.error('[WS] Send message error:', err);
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setError('Failed to send message');
      return false;
    }
  }, [partnerId, token]);

  // Edit a message
  const editMessage = useCallback(async (messageId: string, content: string): Promise<boolean> => {
    if (!content.trim()) return false;

    // Optimistic update
    const originalMessage = messages.find(m => m.id === messageId);
    if (!originalMessage) return false;

    setMessages(prev => prev.map(m =>
      m.id === messageId
        ? { ...m, content: content.trim(), edited: true }
        : m
    ));

    try {
      const response = await fetch(`${API_BASE}/api/messages/${messageId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: content.trim() }),
      });

      if (!response.ok) {
        // Rollback
        setMessages(prev => prev.map(m =>
          m.id === messageId ? originalMessage : m
        ));
        setError('Failed to edit message');
        return false;
      }
      return true;
    } catch (err) {
      // Rollback
      setMessages(prev => prev.map(m =>
        m.id === messageId ? originalMessage : m
      ));
      setError('Failed to edit message');
      return false;
    }
  }, [messages, token]);

  // Delete a message
  const deleteMessage = useCallback(async (messageId: string, opts?: { hard?: boolean }): Promise<boolean> => {
    // Optimistic update
    const originalMessages = [...messages];
    setMessages(prev => prev.filter(m => m.id !== messageId));

    try {
      // hard=true → backend purges the row entirely (admin-only).
      const qs = opts?.hard ? '?hard=true' : '';
      const response = await fetch(`${API_BASE}/api/messages/${messageId}${qs}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
        },
      });

      if (!response.ok) {
        // Rollback
        setMessages(originalMessages);
        setError('Failed to delete message');
        return false;
      }
      return true;
    } catch (err) {
      // Rollback
      setMessages(originalMessages);
      setError('Failed to delete message');
      return false;
    }
  }, [messages, token]);

  // Toggle reaction on a message
  const toggleReaction = useCallback(async (messageId: string, emoji: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE}/api/messages/${messageId}/reactions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emoji }),
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => prev.map(m =>
          m.id === messageId ? { ...m, reactions: data.reactions } : m
        ));
        return true;
      }
      return false;
    } catch (err) {
      setError('Failed to toggle reaction');
      return false;
    }
  }, [token]);

  // Send typing indicator with debounce (500ms) and auto-stop (3s)
  const sendTyping = useMemo(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    return () => {
      const now = Date.now();

      // Debounce: only send if 500ms since last send
      if (now - lastTypingSentRef.current < TYPING_DEBOUNCE) {
        return;
      }

      // Clear existing debounce
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      debounceTimer = setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'typing' }));
          lastTypingSentRef.current = Date.now();

          // Auto-stop typing after 3 seconds of inactivity
          if (typingStopTimeoutRef.current) {
            clearTimeout(typingStopTimeoutRef.current);
          }
          typingStopTimeoutRef.current = setTimeout(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'typing_stop' }));
            }
          }, TYPING_AUTO_STOP);
        }
      }, TYPING_DEBOUNCE);
    };
  }, []);

  // Mark conversation as read
  const markAsRead = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'read' }));
    } else if (token) {
      // REST fallback
      fetch(`${API_BASE}/api/messages/conversations/${partnerId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
      }).catch(() => {});
    }
  }, [partnerId, token]);

  // Load more messages (cursor-based pagination)
  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || messages.length === 0) return;

    setLoadingMore(true);
    const oldestMessage = messages[0];

    try {
      await loadMessagesViaREST(oldestMessage.id);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, messages, loadMessagesViaREST]);

  // Manual refresh
  const refresh = useCallback(async () => {
    if (connected && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'sync' }));
    } else {
      await loadMessagesViaREST();
    }
  }, [connected, loadMessagesViaREST]);

  // Mark as read when opening conversation
  useEffect(() => {
    if (partnerId && connected) {
      markAsRead();
    }
  }, [partnerId, connected, markAsRead]);

  // Connect on mount
  useEffect(() => {
    mountedRef.current = true;

    if (token && partnerId) {
      // First load via REST for immediate data
      loadMessagesViaREST();
      // Then establish WebSocket for real-time
      connect();
    }

    return () => {
      mountedRef.current = false;
      // Cleanup
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      clearHeartbeat();
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (typingStopTimeoutRef.current) {
        clearTimeout(typingStopTimeoutRef.current);
      }
    };
    // Only re-establish the WS when the conversation partner or token
    // actually changes. The callback deps were causing this effect to
    // re-run on every parent render, producing a connect/disconnect
    // loop visible in the console as repeated "[WS] Connected" logs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId, token]);

  return {
    messages,
    connected,
    connecting,
    otherUserTyping,
    otherUserOnline,
    error,
    hasMore,
    loadingMore,
    sendMessage,
    editMessage,
    deleteMessage,
    toggleReaction,
    sendTyping,
    markAsRead,
    loadMore,
    refresh,
  };
}

export default useRealtimeMessages;
